import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'blocks/Button';
import Card from 'blocks/Card';
import { DatePicker, RadioButton } from 'components/Form';
import { RadioButtonInterface } from 'components/Form/RadioButton/radio.interface';
import Loading from 'components/Loading';
import Paths from 'helper/paths';
import { CabinClass } from 'helper/selectConstant';
import { useQueryParams } from 'hooks/useQueryParams';
import i18next from 'i18next';
import AirportInterface from 'models/flight/airport.interface';
import FlightPassengersInterface from 'models/flight/flightPassengers.interface';
import { CabinType, Maybe } from 'models/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { flightFormDataState, setForm, updateForm } from 'store/flight/flightSlice';
import DepartureArrivalField from './departureArrivalField';
import * as S from './flightForm.styles';

enum FlightType {
  twoWay = 'twoWay',
  oneWay = 'oneWay',
}

const flightTypes = [
  {
    title: i18next.t('pages.services.flight.pages.form.fields.flightType.options.twoWay'),
    value: FlightType.twoWay,
    checked: false,
  },
  {
    title: i18next.t('pages.services.flight.pages.form.fields.flightType.options.oneWay'),
    value: FlightType.oneWay,
    checked: false,
  },
] as RadioButtonInterface[];

const FlightForm = ({ discountType }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [discountTicketActive, setDiscountTicketActive] = useState<any>();
  const formDataState = useAppSelector(flightFormDataState);

  const [flightType, setFlightType] = useState<FlightType>(FlightType.twoWay);
  const query = useQueryParams();
  const [isReady, setIsReady] = useState<boolean>(false);
  useEffect(() => {
    setIsReady(false);
    setFlightType(!!query.get('returnDateTime') ? FlightType.twoWay : FlightType.oneWay);
    setDiscountTicketActive(query.get('discountTicketId') || 1);
    dispatch(
      setForm({
        kind: (query.get('kind') as CabinType) || CabinClass[1].value,
        departure: query.get('departure') || '',
        destination: query.get('destination') || '',
        dateTime: query.get('dateTime') || '',
        returnDateTime: query.get('returnDateTime') || '',
        discountTicketId: query.get('discountTicketId') || '',
        adtCount: 0,
      })
    );
    setIsReady(true);
  }, [query]);

  useEffect(() => {
    let discountTickets = discountType;
    setDiscountTicketActive(discountTickets);
  }, [discountType]);

  const setDepartureArrival = ({
    departure,
    arrival,
  }: {
    departure: Maybe<AirportInterface>;
    arrival: Maybe<AirportInterface>;
  }) => {
    dispatch(updateForm({ key: 'departure', value: departure?.code || '' }));
    dispatch(updateForm({ key: 'destination', value: arrival?.code || '' }));
  };

  const updatePassengersCount = (passengersCount: FlightPassengersInterface) => {
    Object.keys(passengersCount).forEach((e) => {
      dispatch(updateForm({ key: e, value: passengersCount[e] }));
    });
  };

  const goToSearch = async () => {
    const formData = { ...formDataState, discountTicketId: discountTicketActive };
    if (flightType === FlightType.oneWay) {
      dispatch(
        updateForm({
          key: 'returnDateTime',
          value: '',
        })
      );
      delete formData.returnDateTime;
    }
    navigate(Paths.Services.discountBook().route + '?' + Paths.ObjectToQueryParams({ ...formData, adtCount: 0 }));
  };

  const DateField = () => {
    return (
      <S.DateFieldWrapper>
        <S.DateFieldContainer>
          <S.DateField>
            <DatePicker
              label={t('pages.services.discount.pages.form.fields.wentDate.label')}
              minDate={new Date().getTime()}
              defaultValue={formDataState.dateTime}
              onChange={(value) => {
                dispatch(
                  updateForm({
                    key: 'dateTime',
                    value: value,
                  })
                );
              }}
            />
          </S.DateField>
          {flightType === FlightType.twoWay ? (
            <S.DateField>
              <DatePicker
                label={t('pages.services.flight.pages.form.fields.returnDate.label')}
                minDate={new Date(formDataState.dateTime || '').getTime()}
                defaultValue={formDataState.returnDateTime || formDataState.dateTime}
                onChange={(value) =>
                  dispatch(
                    updateForm({
                      key: 'returnDateTime',
                      value: value,
                    })
                  )
                }
              />
            </S.DateField>
          ) : null}
        </S.DateFieldContainer>
      </S.DateFieldWrapper>
    );
  };

  return (
    <Card>
      {isReady ? (
        <S.Form>
          <S.FlightTypesWrapper>
            <RadioButton
              items={flightTypes}
              defaultValue={flightType}
              onChange={(value) => {
                setFlightType(value);
              }}
            />
          </S.FlightTypesWrapper>
          <S.FieldsRow>
            <S.DepartureArrival>
              <DepartureArrivalField
                defaultDeparture={formDataState.departure}
                defaultArrival={formDataState.destination}
                onChange={(value) => setDepartureArrival(value)}
              />
            </S.DepartureArrival>
            <S.Field>
              <S.DateFieldParent>
                <DateField />
              </S.DateFieldParent>
            </S.Field>
          </S.FieldsRow>

          <Button styleType="default" className=" fs-xs" onClick={goToSearch}>
            {t('pages.services.flight.pages.form.submitCTA')}
          </Button>
        </S.Form>
      ) : (
        <Loading />
      )}
    </Card>
  );
};

export default FlightForm;
